import { createFileRoute, notFound, useNavigate, useRouter } from "@tanstack/react-router";
import { loadSettlementDetail, deleteSettlementFn } from "@/lib/server-fns/settlements";
import {
  Container,
  Heading,
  Card,
  Text,
  VStack,
  HStack,
  Button,
  Separator,
  Flex,
  Box,
} from "@chakra-ui/react";
import { LuArrowLeft, LuTrash2, LuArrowRight } from "react-icons/lu";
import { useState } from "react";
import { formatDate, formatAmount } from "@/lib/format";
import { UserNameCell } from "@/components/user-name-cell";
import { getUser, getDisplayName } from "@/lib/user-utils";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { showErrorToast } from "@/lib/toast-utils";
import { NotFoundContent, ErrorContent } from "@/components/error-page";

export const Route = createFileRoute("/_authenticated/settlements/$id")({
  loader: async ({ params }) => {
    try {
      return await loadSettlementDetail({ data: { id: params.id } });
    } catch (error) {
      if (error instanceof Error && error.message === "Settlement not found") {
        throw notFound();
      }
      throw error;
    }
  },
  notFoundComponent: () => (
    <NotFoundContent
      title="Settlement not found"
      description="This settlement doesn't exist or you don't have access to it."
      backHref="/expenses"
      backLabel="Back to Activity"
    />
  ),
  errorComponent: ({ error, reset }) => <ErrorContent error={error} reset={reset} />,
  component: SettlementDetail,
});

function SettlementDetail() {
  const { settlement, friends, currentUser } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const ctx = { currentUser, friends };
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fromUser = getUser(settlement.fromUserId, ctx);
  const toUser = getUser(settlement.toUserId, ctx);
  const fromIdentifier = fromUser.email || settlement.fromUserId;
  const toIdentifier = toUser.email || settlement.toUserId;
  const formattedAmount = formatAmount(settlement.amount, settlement.currency, { absolute: false });

  const handleDelete = async () => {
    try {
      await deleteSettlementFn({ data: { id: settlement.id } });
      navigate({ to: "/expenses" });
    } catch (error) {
      showErrorToast("Failed to delete settlement", error);
    }
  };

  return (
    <Container maxW="4xl" px={{ base: "0", md: "4" }}>
      <HStack mb="6" px={{ base: "4", md: "0" }} justify="space-between">
        <Button variant="ghost" onClick={() => router.history.back()}>
          <LuArrowLeft size={16} />
          Back to Activity
        </Button>
        <Button variant="ghost" colorPalette="red" size="sm" onClick={() => setDeleteOpen(true)}>
          <LuTrash2 size={16} />
          Delete
        </Button>
      </HStack>

      <Card.Root variant="outline">
        <Card.Body p={{ base: "4", md: "8" }}>
          <VStack gap="4" align="stretch">
            <Flex
              direction={{ base: "column-reverse", md: "row" }}
              justify="space-between"
              align={{ base: "stretch", md: "flex-start" }}
              gap={{ base: "2", md: "4" }}
            >
              <Box>
                <Heading as="h2" size="lg" mb="1">
                  Settlement
                </Heading>
                <Text fontSize="sm" color="fg.muted">
                  {getDisplayName(settlement.fromUserId, ctx)} paid{" "}
                  {getDisplayName(settlement.toUserId, ctx)}
                </Text>
              </Box>
              <Heading as="h1" size="2xl" color="loonie.fg" flexShrink={0}>
                {formattedAmount}
              </Heading>
            </Flex>

            <Separator />

            <Flex gap={{ base: "4", md: "8" }} wrap="wrap">
              <VStack gap="1" align="start">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Date
                </Text>
                <Text fontSize="lg">{formatDate(settlement.date)}</Text>
              </VStack>

              <VStack gap="1" align="start">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Currency
                </Text>
                <Text fontSize="lg">{settlement.currency}</Text>
              </VStack>
            </Flex>

            <Separator />

            <Box>
              <Text fontSize="lg" fontWeight="medium" mb="4">
                Participants
              </Text>
              <Flex align="center" gap={{ base: "3", md: "4" }} wrap="wrap">
                <UserNameCell
                  name={fromUser.name}
                  identifier={fromIdentifier}
                  displayName={getDisplayName(settlement.fromUserId, ctx)}
                />
                <LuArrowRight size={16} color="gray" />
                <UserNameCell
                  name={toUser.name}
                  identifier={toIdentifier}
                  displayName={getDisplayName(settlement.toUserId, ctx)}
                />
              </Flex>
            </Box>
          </VStack>
        </Card.Body>
      </Card.Root>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Settlement"
        description={`Are you sure you want to delete this settlement? This will reverse the associated ledger transfer and cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </Container>
  );
}
